import { useCallback, useState } from "react";
import { NavLink, useNavigate } from "react-router";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Plus, Search } from "lucide-react";
import t from "@/lang/lang";
import { Tabs, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { v4 } from "uuid";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { useProcessesProcessShowQuery } from "@/services/backend/processes/api/process-api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";
import { ProcessTopic } from "@/services/backend/process-topic/components/process-topic";
import {
  useQpmOfferChangeDocumentProfileMutation,
  useQpmOfferCreateFromOfferMutation,
  useQpmOfferCreateMutation,
  useQpmOfferListQuery,
  useQpmOfferReloadOfferConfigSnapshotMutation,
} from "@/services/backend/qpm/offers/api/offer-api";
import { TabsContent } from "@radix-ui/react-tabs";
import { CreateFromCalculation } from "@/routes/processes/[processId]/qpm/offers/_components/create-from-calculation";
import {
  buildCreateOfferDocumentProfile,
  createDefaultOfferDocumentProfileDraft,
  createPatchFromCreateProfile,
  OfferDocumentProfileForm,
  offerTypeLabel,
  templateScenarioLabel,
  validateOfferDocumentProfileDraft,
} from "@/routes/processes/[processId]/qpm/offers/_components/offer-document-profile-form";
import { Badge } from "@/shared/components/ui/badge";
import { CatalogSourceSelect } from "@/routes/base-data/processes/qpm/catalog/_components/catalog-source-select";
import { useProcessCatalogSelection } from "@/services/backend/qpm/catalog/hooks/use-process-catalog-selection";
import { useActiveCompany } from "@/shared/lib/active-company/active-company-provider";

export type CreateOfferDialogProps = {
  customerId: string;
  processId: string;
};

export function CreateOfferDialog({
  customerId,
  processId,
}: CreateOfferDialogProps) {
  const [open, setOpen] = useState(false);
  const [documentProfileDraft, setDocumentProfileDraft] = useState(
    createDefaultOfferDocumentProfileDraft(),
  );
  const navigate = useNavigate();
  const documentProfileError =
    validateOfferDocumentProfileDraft(documentProfileDraft);
  const documentProfile = buildCreateOfferDocumentProfile(documentProfileDraft);

  const closeDialogAndNavigate = useCallback(
    (offerId: string) => {
      setOpen(false);
      navigate(offerId);
    },
    [navigate],
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="mr-2 h-5 w-5" />
          <span>{t("Angebot")}</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle>{t("Neues Angebot erstellen")}</DialogTitle>
          <DialogDescription>
            {t(
              "Angebote können entweder komplett neu, auf Basis einer Kalkulation oder auf Basis eines alten Angebots erstellt werden.",
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-md border p-4">
          <h3 className="text-lg font-semibold">{t("Angebotsart")}</h3>
          <p className="text-muted-foreground text-sm">
            {t("Die Auswahl gilt fur alle Erstellungswege.")}
          </p>
          <div className="mt-4">
            <OfferDocumentProfileForm
              mode="create"
              value={documentProfileDraft}
              onChange={setDocumentProfileDraft}
              validationMessage={documentProfileError}
            />
          </div>
        </div>
        <Tabs defaultValue="manual">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="manual">{t("Neu Anlegen")}</TabsTrigger>
            <TabsTrigger value="from-calculation">
              {t("Kalkulationen")}
            </TabsTrigger>
            <TabsTrigger value="from-old">{t("Alte Angebote")}</TabsTrigger>
          </TabsList>
          <TabsContent value="manual">
            <CreateManually
              customerId={customerId}
              processId={processId}
              documentProfile={documentProfile}
              isDocumentProfileValid={!documentProfileError}
              onCreated={closeDialogAndNavigate}
            />
          </TabsContent>
          <TabsContent value="from-calculation">
            <CreateFromCalculation
              processId={processId}
              documentProfile={documentProfile}
              isDocumentProfileValid={!documentProfileError}
              onCreated={closeDialogAndNavigate}
            />
          </TabsContent>
          <TabsContent value="from-old">
            <CreateFromOld
              processId={processId}
              documentProfile={documentProfile}
              isDocumentProfileValid={!documentProfileError}
              onCreated={closeDialogAndNavigate}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function CreateManually({
  customerId,
  processId,
  documentProfile,
  isDocumentProfileValid,
  onCreated,
}: {
  customerId: string;
  processId: string;
  documentProfile: ReturnType<typeof buildCreateOfferDocumentProfile>;
  isDocumentProfileValid: boolean;
  onCreated: (offerId: string) => void;
}) {
  const [create, { isLoading, error }] = useQpmOfferCreateMutation();
  const { data: process } = useProcessesProcessShowQuery({ id: processId });
  const {
    catalogId,
    setCatalogId,
    isLoading: isLoadingCatalogs,
    error: catalogError,
  } = useProcessCatalogSelection(customerId);

  const onClick = async () => {
    if (isLoading || !isDocumentProfileValid) {
      return;
    }

    try {
      const id = v4();
      const created = await create({
        id,
        customerId,
        processId,
        catalogId: catalogId || undefined,
        companyId: process?.companyId ?? "",
        branchId: process?.branches[0] ?? "",
        documentProfile,
      }).unwrap();
      onCreated(created.id);
    } catch {
      // handled by RTK error state
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Neu Anlegen")}</CardTitle>
        <CardDescription>
          {t(
            "Erstellt eine Kalkulation mit den Basisdaten des Prozesses und des Kunden.",
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="mb-4 space-y-2">
          <div className="text-sm font-medium">{t("Katalog")}</div>
          <CatalogSourceSelect
            value={catalogId}
            onChange={setCatalogId}
            customerId={customerId}
          />
          {isLoadingCatalogs && (
            <div className="text-muted-foreground text-xs">
              {t("Kataloge werden geladen")}
            </div>
          )}
          <RTKQueryErrorAlert error={catalogError} />
        </div>
        <RTKQueryErrorAlert error={error} />
      </CardContent>
      <CardFooter className="justify-start">
        <Button onClick={onClick} disabled={isLoading}>
          {t("Erstellen")}
        </Button>
      </CardFooter>
    </Card>
  );
}

function CreateFromOld(props: {
  processId: string;
  documentProfile: ReturnType<typeof buildCreateOfferDocumentProfile>;
  isDocumentProfileValid: boolean;
  onCreated: (offerId: string) => void;
}) {
  const { processId, documentProfile, isDocumentProfileValid, onCreated } =
    props;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Auf Basis alter Kalkulation")}</CardTitle>
        <CardDescription>
          {t(
            "Kopiert die Daten der alten Kalkulation. Es werden nur Kalkulationen von abgeschlossenen Vorgängen angezeigt.",
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <OldOffersTable
          processId={processId}
          documentProfile={documentProfile}
          isDocumentProfileValid={isDocumentProfileValid}
          onCreated={onCreated}
        />
      </CardContent>
    </Card>
  );
}

function OldOffersTable(props: {
  processId: string;
  documentProfile: ReturnType<typeof buildCreateOfferDocumentProfile>;
  isDocumentProfileValid: boolean;
  onCreated: (offerId: string) => void;
}) {
  const { processId, documentProfile, isDocumentProfileValid, onCreated } =
    props;
  const { data: process } = useProcessesProcessShowQuery({ id: processId });

  const {
    data: list,
    isLoading,
    error,
  } = useQpmOfferListQuery({
    process: {
      active: false,
      values: [],
    },
    customer: {
      active: true,
      values: [process?.customerId || ""],
    },
  });

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (isLoading) {
    return (
      <Table className="animate-pulse">
        <TableHeader>
          <TableRow>
            <TableHead>{t("Datum")}</TableHead>
            <TableHead>{t("Nummer")}</TableHead>
            <TableHead>{t("Angebotsart")}</TableHead>
            <TableHead className="w-full">{t("Vorgang Thema")}</TableHead>
            <TableHead>{t("Aktionen")}</TableHead>
          </TableRow>
        </TableHeader>
      </Table>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t("Datum")}</TableHead>
          <TableHead>{t("Nummer")}</TableHead>
          <TableHead>{t("Angebotsart")}</TableHead>
          <TableHead className="w-full">{t("Vorgang Thema")}</TableHead>
          <TableHead>{t("Aktionen")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {list?.items?.map((offer) => {
          const offerType = offer.documentProfile?.offerType?.value;
          const templateScenario = offer.documentProfile?.templateScenario;

          return (
            <TableRow key={offer.id}>
              <TableCell>
                {new Date(offer.createdAt).toLocaleDateString()}
              </TableCell>
              <TableCell>
                <DocumentNumberById documentId={offer.id} />
              </TableCell>
              <TableCell>
                <div className="flex flex-col gap-1">
                  <Badge variant="secondary">
                    {offerType ? offerTypeLabel(offerType) : t("Unbekannt")}
                  </Badge>
                  {templateScenario && (
                    <span className="text-muted-foreground text-xs">
                      {templateScenarioLabel(templateScenario)}
                    </span>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <ProcessTopic processId={processId} />
              </TableCell>
              <TableCell>
                <div className="flex space-x-2">
                  {offer.processArtefact && (
                    <NavigateToOffer
                      currentProcessId={processId}
                      offerProcessId={offer.processArtefact.processId}
                      offerId={offer.id}
                    />
                  )}
                  <CopyOfferButton
                    offerId={offer.id}
                    processId={processId}
                    documentProfile={documentProfile}
                    isDocumentProfileValid={isDocumentProfileValid}
                    onCopy={onCreated}
                  />
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

function NavigateToOffer(props: {
  currentProcessId: string;
  offerProcessId: string;
  offerId: string;
}) {
  const { currentProcessId, offerProcessId, offerId } = props;

  // If the offer is part of the same process,
  // simply navigate to the offer
  if (currentProcessId === offerProcessId) {
    return (
      <NavLink to={offerId}>
        <Button variant="ghost">
          <Search className="h-4 w-4" />
        </Button>
      </NavLink>
    );
  }

  // If the offer is part of a different process,
  // show a warning and then navigate to the offer
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="ghost">
          <Search className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Achtung")}</DialogTitle>
        </DialogHeader>
        <DialogDescription>
          {t(
            "Dieses Angebot ist Teil eines anderen Vorgangs. Sie müssen nachdem Sie das Angebot angesehen haben zum aktuellen Vorgang zurückkehren.",
          )}
        </DialogDescription>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("Abbrechen")}</Button>
          </DialogClose>
          <NavLink to={`/processes/qpm/${offerProcessId}/offers/${offerId}`}>
            <Button>{t("Vorgang wechseln")}</Button>
          </NavLink>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export type CopyOfferButtonProps = {
  offerId: string;
  processId: string;
  documentProfile: ReturnType<typeof buildCreateOfferDocumentProfile>;
  isDocumentProfileValid: boolean;
  onCopy: (offerId: string) => void;
};

export function CopyOfferButton({
  offerId,
  processId,
  documentProfile,
  isDocumentProfileValid,
  onCopy,
}: CopyOfferButtonProps) {
  const { companyId } = useActiveCompany();
  const { data: process } = useProcessesProcessShowQuery({ id: processId });
  const [createFromOffer, { isLoading: isCopyLoading, error: copyError }] =
    useQpmOfferCreateFromOfferMutation();
  const [reloadSnapshot, { isLoading: isReloadLoading, error: reloadError }] =
    useQpmOfferReloadOfferConfigSnapshotMutation();
  const [
    changeDocumentProfile,
    { isLoading: isPatchLoading, error: patchError },
  ] = useQpmOfferChangeDocumentProfileMutation();

  const onClick = async () => {
    if (
      isCopyLoading ||
      isPatchLoading ||
      isReloadLoading ||
      !isDocumentProfileValid
    ) {
      return;
    }

    try {
      const copied = await createFromOffer({
        id: v4(),
        processId,
        sourceOfferId: offerId,
        companyId,
        branchId:
          ((process as { branches?: string[] } | undefined)?.branches ??
            [])[0] ?? "",
      }).unwrap();

      await changeDocumentProfile({
        offerId: copied.id,
        documentProfile: createPatchFromCreateProfile(documentProfile),
      }).unwrap();

      await reloadSnapshot({ offerId: copied.id }).unwrap();

      onCopy(copied.id);
    } catch {
      // handled by RTK error state
    }
  };

  return (
    <div className="flex flex-col items-start gap-2">
      <Button
        disabled={
          isCopyLoading ||
          isPatchLoading ||
          isReloadLoading ||
          !isDocumentProfileValid
        }
        onClick={onClick}
      >
        {t("Kopieren")}
      </Button>
      <RTKQueryErrorAlert error={reloadError} />
      <RTKQueryErrorAlert error={copyError} />
      <RTKQueryErrorAlert error={patchError} />
    </div>
  );
}
